import { useMemoizedFn } from 'ahooks';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { getPixelRatio } from '../utils/common';
import { getScaleByWidth } from '../utils/pdf';
import { perfStart } from '../utils/perfMetrics';

// 缩放常量（与 docReaderContext 文档阅读器 scale 逻辑一致）
export const MIN_SCALE = 0.05; // 最小缩放 5%
export const MAX_SCALE = 5; // 最大缩放 500%
const LIMIT_SCALE = 0.2; // 低于此值时使用小步长
const ZOOM_STEP = 0.2; // 常规步长（scale ≥ 0.2 时）
const ZOOM_STEP_SMALL = 0.05; // 小步长（scale < 0.2 时）

/**
 * 根据当前 scale 与方向 delta 计算实际步长，保证不越界且可逆。
 * - scale < 0.2：步长固定 ±0.05
 * - scale ≥ 0.2：默认 ±0.2；缩小且一步会跨入 0.2 以下时改用 -0.05
 * - 边界时返回恰好到 0.05 或 5 的步长
 */
function getDelta(scale: number, delta: number): number {
    let step: number;
    if (scale < LIMIT_SCALE) {
        step = delta >= 0 ? ZOOM_STEP_SMALL : -ZOOM_STEP_SMALL;
    } else if (delta < 0 && scale + delta < LIMIT_SCALE) {
        step = -ZOOM_STEP_SMALL;
    } else {
        step = delta;
    }
    const next = scale + step;
    if (next <= MIN_SCALE) return MIN_SCALE - scale;
    if (next >= MAX_SCALE) return MAX_SCALE - scale;
    return step;
}

/**
 * 计算 canvas 渲染应使用的 tier scale。
 *
 * Tier 序列：baseScale × 2^n（n 为整数），每档覆盖 [tier/dpr, tier×dpr] 的视觉范围，
 * 在此范围内 CSS transform 不超出像素预算，无需重绘。
 * DPR=2 时每档覆盖 4× 缩放范围，大幅减少重绘次数。
 *
 * @param targetScale 目标视觉 scale
 * @param baseScale   初始 scale（锚点，tier 以此为 2^0）
 * @param dpr         设备像素比
 */
function calcRenderTier(targetScale: number, baseScale: number, dpr: number): number {
    if (baseScale <= 0 || dpr <= 0 || targetScale <= 0) return targetScale;
    // 找最小 n 使 tier × dpr ≥ targetScale，保证 canvas 物理像素始终不欠缺。
    // ceil 优于 round：round 在 tier 边界取"最近"，缩小方向可能取更高 tier 浪费内存；
    // ceil 在放大时取恰好满足质量的最低 tier，在缩小时取更小 tier 降低开销。
    const n = Math.ceil(Math.log2(targetScale / (baseScale * dpr)));
    const tier = baseScale * 2 ** n;
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, tier));
}

/**
 * 计算下一步 scale，含浮点修正与边界限制。
 * 当 scale 存在浮点误差时以 toFixed(2) 为基准，仍通过 getDelta 取步长，保证可逆性。
 */
function calculateScale(scale: number, delta: number): number {
    const fixed = Number(scale.toFixed(2));
    const base = scale !== fixed ? fixed : scale;
    const step = getDelta(base, delta);
    const ret = base + step;
    const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(Number(ret).toFixed(2))));
    return clamped;
}
/**
 * PDF 视口 Hook 的 Options 接口
 */
interface UsePdfViewportOptions {
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js pdfDoc
    pdfDoc: any; // PDF 文档对象
    pageNum: number; // 当前页码
    containerRef: RefObject<HTMLElement>; // 容器元素引用
    /** 全屏状态，变化时需重新计算初始缩放（兼容 Safari 全屏切换后内容不展示） */
    screenState?: 'no-full' | 'full';
}

/**
 * PDF 视口和缩放管理 Hook
 *
 * 核心功能：
 * 1. 根据容器宽度自动计算初始缩放比例
 * 2. 提供缩放控制方法（放大、缩小、重置）
 * 3. 通过 CSS 变量同步缩放比例到 DOM
 */

export function usePdfViewport(options: UsePdfViewportOptions) {
    const { pdfDoc, pageNum, containerRef, screenState } = options;

    // 状态管理
    const [scale, setScaleState] = useState<number>(0); // 当前缩放比例（视觉 scale，驱动布局）
    const [renderScale, setRenderScale] = useState<number>(0); // canvas 渲染 scale（仅超阈值时更新，避免频繁重绘）
    const [initScale, setInitScale] = useState<number>(1); // 初始缩放比例
    const isInitializedRef = useRef(false); // 是否已初始化标记
    const lastKeyRef = useRef<string>(''); // 上次的 PDF 文档+页码组合键，用于检测变化
    const initScaleRef = useRef<number>(1); // 与 initScale state 同步的 ref，供 updateRenderScaleIfNeeded 使用
    // scale ref：与 scale state 同步，供 zoomIn/zoomOut 避免 stale closure 读到旧值
    const scaleRef = useRef(0);
    scaleRef.current = scale;
    // 用于检测 screenState 转换方向（full → no-full）
    const prevScreenStateRef = useRef<string | undefined>(screenState);
    // 退出全屏标记：触发后在下一次初始化时检查是否需要重置 scale
    const exitingFullscreenRef = useRef(false);

    /**
     * 安全的缩放比例计算
     * 限制缩放范围在 [MIN_SCALE, MAX_SCALE] 即 [0.05, 5]
     */
    const safeScale = (newScale: number) => {
        if (newScale < MIN_SCALE) return MIN_SCALE;
        if (newScale > MAX_SCALE) return MAX_SCALE;
        return newScale;
    };

    /**
     * 检查并按需更新 renderScale（基于 tier 分级）
     *
     * 核心原理：tier 序列为 initScale × 2^n，每档覆盖 [tier/dpr, tier×dpr] 的无损缩放区间。
     * 当 scale 未离开当前 tier 的区间时，CSS transform 补偿即可，无需 canvas 重绘。
     * 超出区间时跳到最近 tier，触发一次重绘；DPR=2 时每档覆盖 4× 范围。
     */
    const updateRenderScaleIfNeeded = (newScale: number) => {
        const dpr = getPixelRatio();
        const base = initScaleRef.current;
        setRenderScale((prev) => {
            if (prev <= 0) return newScale; // 未初始化时直接设置
            const ratio = newScale / prev;
            if (ratio <= dpr && ratio >= 1 / dpr) return prev; // 阈值内：保持当前 renderScale
            return calcRenderTier(newScale, base, dpr); // 超阈值：跳到最近 tier
        });
    };

    const setScale = useMemoizedFn((newScale: number) => {
        const safe = safeScale(newScale);
        perfStart('scale', String(pageNum), { targetScale: safe });
        setScaleState(safe);
        setRenderScale(safe); // pinch 直接触发完整重绘（不走 tier 阈值）
    });

    /**
     * 统一缩放调度入口（非对称 Tier 阈值）
     *
     * - 立即更新视觉 scale（布局无延迟）
     * - 放大超过 1.2× 或缩小超过 0.5× 时跳到最近 Tier 触发重绘，否则保持当前 renderScale
     *   使用 CSS transform 补偿，大幅减少重绘次数（DPR=2 时每档覆盖 4× 范围）
     */
    const applyScale = useMemoizedFn((newScale: number) => {
        const safe = safeScale(newScale);
        perfStart('scale', String(pageNum), { targetScale: safe });
        setScaleState(safe);
        setRenderScale((prev) => {
            if (prev <= 0) return safe;
            const ratio = safe / prev;
            if (ratio > 1.2 || ratio < 0.5) {
                return calcRenderTier(safe, initScaleRef.current, getPixelRatio());
            }
            return prev;
        });
    });

    /**
     * 放大方法
     * 全屏模式：applyScale（Tier 机制，减少虚拟列表重绘）
     * 非全屏模式：setScale（立即更新 renderScale，确保 DOM 尺寸与视觉一致，横向滚动条正确出现）
     */
    const zoomIn = useMemoizedFn(() => {
        const current = scaleRef.current;
        if (!Number.isFinite(current) || current <= 0) return;
        const next = calculateScale(current, ZOOM_STEP);
        if (next === current) return; // 已到最大值
        if (screenState === 'full') {
            applyScale(next);
        } else {
            setScale(next);
        }
    });

    /**
     * 缩小方法
     * 与 zoomIn 对称。
     */
    const zoomOut = useMemoizedFn(() => {
        const current = scaleRef.current;
        if (!Number.isFinite(current) || current <= 0) return;
        const next = calculateScale(current, -ZOOM_STEP);
        if (next === current) return; // 已到最小值
        if (screenState === 'full') {
            applyScale(next);
        } else {
            setScale(next);
        }
    });

    /**
     * 重置缩放方法
     * 恢复到初始缩放比例。
     */
    const zoomReset = useMemoizedFn(() => {
        if (initScale) {
            if (screenState === 'full') {
                applyScale(initScale);
            } else {
                setScale(initScale);
            }
        }
    });

    /**
     * 初始化缩放比例 Effect
     *
     * 核心逻辑：
     * 1. 检查前置条件（PDF 文档、页码、容器）
     * 2. 检测 PDF 文档或页码变化，重置初始化状态
     * 3. 获取页面对象并计算适合容器宽度的缩放比例
     * 4. 处理容器未准备好的情况（延迟重试）
     * 5. 设置初始缩放和当前缩放
     */
    useEffect(() => {
        // 1. 前置检查：必须满足所有条件才能初始化
        if (!(pdfDoc && pageNum) || pageNum < 1 || !containerRef.current) {
            isInitializedRef.current = false;
            if (!(pdfDoc && pageNum) || pageNum < 1) {
                // PDF 文档或页码无效时，重置缩放为 0
                setScaleState(0);
                setRenderScale(0);
            }
            return;
        }

        // 2. 检测 PDF 文档或全屏状态变化（全屏切换后 Safari 等需重新计算缩放与布局）
        const currentKey = `${pdfDoc?.fingerprints?.[0] || 'unknown'}-${screenState ?? ''}`;

        if (lastKeyRef.current !== currentKey) {
            isInitializedRef.current = false;
            lastKeyRef.current = currentKey;
        }

        // 3. 如果已初始化，跳过（避免用户缩放或切换页码时被重置）
        // 这样切换页码时不会重置 scale，保持用户设置的缩放比例
        if (isInitializedRef.current) {
            return;
        }

        // 4. 获取页面并计算初始缩放
        pdfDoc
            .getPage(pageNum)
            // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
            .then((page: any) => {
                if (!containerRef.current) {
                    return;
                }

                // 获取实际内容容器的宽度
                // 单页模式：使用 .pdf-list-horizontal 的宽度（实际内容容器）
                // 全屏模式：使用 .pdf-content-wrap 的宽度
                // 如果都找不到，则使用 containerRef
                const contentWrap = containerRef.current.querySelector('.pdf-content-wrap') as HTMLElement;
                const horizontalContainer = contentWrap?.querySelector('.pdf-list-horizontal') as HTMLElement;
                // 优先使用单页模式的容器宽度，如果没有则使用 .pdf-content-wrap，最后回退到 containerRef
                const contentContainer = horizontalContainer || contentWrap || containerRef.current;
                const containerWidth = contentContainer.offsetWidth;

                // 5. 处理容器未准备好的情况
                if (containerWidth <= 0) {
                    // 延迟重试：等待容器布局完成
                    // 注意：这里仍然使用 currentKey 检查，确保是同一个 PDF 文档
                    setTimeout(() => {
                        if (containerRef.current && lastKeyRef.current === currentKey && !isInitializedRef.current) {
                            const retryContentWrap = containerRef.current.querySelector(
                                '.pdf-content-wrap'
                            ) as HTMLElement;
                            const retryHorizontalContainer = retryContentWrap?.querySelector(
                                '.pdf-list-horizontal'
                            ) as HTMLElement;
                            const retryContentContainer =
                                retryHorizontalContainer || retryContentWrap || containerRef.current;
                            const retryWidth = retryContentContainer.offsetWidth;
                            if (retryWidth > 0) {
                                let newScale = getScaleByWidth(page, retryWidth);
                                newScale = Math.max(newScale, MIN_SCALE);
                                initScaleRef.current = newScale;
                                setInitScale(newScale);
                                // 仅首次初始化（scale 尚未设置）才更新 scale/renderScale；
                                // 全屏切换等重新初始化时，保留当前 scale，避免覆盖用户缩放偏好
                                if (scaleRef.current <= 0) {
                                    setScaleState(newScale);
                                    updateRenderScaleIfNeeded(newScale);
                                } else if (exitingFullscreenRef.current && scaleRef.current > newScale) {
                                    // 退出全屏后，若当前比例超过非全屏初始比例，重置回初始比例
                                    setScaleState(newScale);
                                    setRenderScale(newScale);
                                }
                                exitingFullscreenRef.current = false;
                                isInitializedRef.current = true;
                            }
                        }
                    }, 100);
                    return;
                }

                // 6. 双重检查：防止并发初始化
                if (isInitializedRef.current) {
                    return;
                }

                // 7. 计算并设置初始缩放
                let newScale = getScaleByWidth(page, containerWidth);
                newScale = Math.max(newScale, MIN_SCALE);
                initScaleRef.current = newScale;
                setInitScale(newScale);
                // 仅首次初始化（scale 尚未设置）才更新 scale/renderScale；
                // 全屏切换等重新初始化时，保留当前 scale，避免覆盖用户缩放偏好
                if (scaleRef.current <= 0) {
                    setScaleState(newScale);
                    updateRenderScaleIfNeeded(newScale);
                } else if (exitingFullscreenRef.current && scaleRef.current > newScale) {
                    // 退出全屏后，若当前比例超过非全屏初始比例，重置回初始比例
                    setScaleState(newScale);
                    setRenderScale(newScale);
                }
                exitingFullscreenRef.current = false;
                isInitializedRef.current = true;
            })
            // biome-ignore lint/suspicious/noExplicitAny: error type
            .catch((_err: any) => {
                isInitializedRef.current = false;
            });
    }, [pdfDoc, pageNum, containerRef, screenState]);

    /**
     * scale state 变化后同步 CSS 变量（供 SVG 渲染和样式计算使用）
     */
    useEffect(() => {
        if (scale && containerRef.current) {
            containerRef.current.style.setProperty('--scale-factor', `${scale}`);
        }
    }, [scale, containerRef]);

    /**
     * 全屏切换时同步 renderScale，消除 CSS transform 残差并防止多页内存溢出。
     *
     * 退出全屏（full → no-full）：
     *   全屏模式使用 applyScale（tier 阈值），renderScale 可能低于 scale。
     *   退出后 cssTransformRatio > 1 会导致横向滚动条 + 内容遮挡。
     *   修复：将 renderScale 对齐到 scale，使 cssTransformRatio = 1。
     *
     * 进入全屏（no-full → full）：
     *   非全屏模式使用 setScale（renderScale = scale），用户可能已放大至较高倍率。
     *   全屏多页模式下所有可见页同时以该 renderScale 渲染，造成内存峰值过高。
     *   修复：通过 tier 计算将 renderScale 限制到合理档位，CSS transform 补偿视觉差异。
     *   代价：进入全屏时各页触发一次重绘（可接受）。
     */
    useEffect(() => {
        const prev = prevScreenStateRef.current;
        prevScreenStateRef.current = screenState;
        if (prev === 'full' && screenState !== 'full' && scaleRef.current > 0) {
            exitingFullscreenRef.current = true;
            // 退出全屏：对齐 renderScale → scale
            setRenderScale(scaleRef.current);
        } else if (prev !== 'full' && screenState === 'full') {
            exitingFullscreenRef.current = false;
            // 进入全屏：将 renderScale 收敛到 tier 档位，避免多页同时高分辨率渲染
            setRenderScale((current) => {
                const s = scaleRef.current;
                if (current <= 0 || s <= 0) return current;
                const dpr = getPixelRatio();
                const tiered = calcRenderTier(s, initScaleRef.current, dpr);
                // 仅在当前 renderScale 高于 tier 值时才降低，不主动升高（不破坏已经合理的值）
                return tiered < current ? tiered : current;
            });
        }
    }, [screenState]);

    return {
        scale,
        renderScale,
        initScale,
        setScale,
        applyScale,
        zoomIn,
        zoomOut,
        zoomReset,
    };
}
